import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutGrid, LogOut, Home, Inbox, Briefcase, Calendar as CalendarIcon } from "lucide-react";

export default function DashboardShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen bg-stone-50 flex" data-testid="dashboard-shell">
      <aside className="w-64 hidden md:flex flex-col bg-stone-900 text-stone-300 sticky top-0 h-screen">
        <Link to="/" className="px-6 py-7 flex items-center gap-3 border-b border-stone-800" data-testid="dashboard-logo">
          <div className="rounded-2xl overflow-hidden bg-white p-2 shadow-sm flex-shrink-0">
            <img
              src="/wynridge-logo.png?v=2"
              alt="Wynridge Exterior Cleaning"
              className="h-14 w-auto object-contain"
            />
          </div>
        </Link>

        <nav className="flex-1 px-4 py-6 space-y-1">
          <SidebarLink to="/dashboard" icon={LayoutGrid} label="My Jobs" active={location.pathname === "/dashboard" || (location.pathname.startsWith("/dashboard/jobs") && !location.pathname.startsWith("/admin"))} testId="sidebar-jobs" />
          {isAdmin && (
            <>
              <div className="px-3 mt-6 mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
                Admin
              </div>
              <SidebarLink
                to="/admin/quotes"
                icon={Inbox}
                label="Quote Requests"
                active={location.pathname === "/admin/quotes"}
                testId="sidebar-admin-quotes"
              />
              <SidebarLink
                to="/admin/jobs"
                icon={Briefcase}
                label="All Jobs"
                active={location.pathname === "/admin/jobs"}
                testId="sidebar-admin-jobs"
              />
              <SidebarLink
                to="/admin/calendar"
                icon={CalendarIcon}
                label="Calendar"
                active={location.pathname === "/admin/calendar"}
                testId="sidebar-admin-calendar"
              />
            </>
          )}
          <div className="px-3 mt-6 mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">
            Public
          </div>
          <SidebarLink to="/" icon={Home} label="Home page" testId="sidebar-home" />
        </nav>

        <div className="p-4 border-t border-stone-800">
          <div className="px-3 py-3 mb-2 bg-stone-800/50 rounded-xl" data-testid="sidebar-user-info">
            <div className="text-xs text-stone-500 uppercase tracking-[0.15em] font-semibold">Signed in as</div>
            <div className="text-sm text-white font-semibold mt-1 truncate">{user?.name}</div>
            <div className="text-xs text-stone-400 truncate">{user?.email}</div>
          </div>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="dashboard-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-stone-300 hover:bg-stone-800 hover:text-white rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="md:hidden bg-stone-900 text-white px-6 py-4 flex items-center justify-between sticky top-0 z-40">
          <Link to="/" className="font-bold" style={{ fontFamily: "Outfit" }}>Wynridge</Link>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="dashboard-logout-mobile-btn"
            className="text-sm font-semibold text-stone-300"
          >
            Sign out
          </button>
        </header>
        <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
          {children}
        </main>
      </div>
    </div>
  );
}

function SidebarLink({ to, icon: Icon, label, active, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
        active ? "bg-stone-800 text-white" : "text-stone-400 hover:bg-stone-800/60 hover:text-white"
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </Link>
  );
}
